import { useState, useCallback, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button, buttonVariants } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Upload,
  FileSpreadsheet,
  Download,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  ArrowRight,
  Plus,
  RefreshCw,
  Smartphone,
  ChevronDown,
  Check,
  Tag,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  parseCSV,
  ParseResult,
  ColumnMapping,
  transformRow,
  ValidationResult,
  getAvailableFields,
  generateTemplateCSV,
  generateTemplateXLSX,
  extractUniqueStages,
  getLeadImportMappingWarnings,
  StageInfo,
  ExistingStage,
  getRandomColor,
  generateSlug,
} from "@/lib/lead-csv-parser";
import { humanizeLeadImportError } from "@/lib/lead-import-errors";
import { normalizeImportPhone, buildImportPhoneVariants } from "@/lib/import-phone";
import { cn } from "@/lib/utils";
import { fetchMemberAccessibleWhatsAppIntegrations } from "@/lib/member-accessible-integrations";
import {
  enqueueUazapiContactsSync,
  fetchUazapiContactsSyncJob,
  isUazapiContactsSyncTerminal,
  type UazapiContactsSyncJobStatus,
} from "@/lib/uazapi-contacts-sync";
import type { LeadTag } from "@/types/lead";
import { TAG_COLORS } from "@/types/lead";
import { LeadTagBadge } from "@/components/leads/LeadTagBadge";

interface LeadCsvImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  funnelId?: string | null;
  onImportComplete: () => void;
}

type ImportStep = "upload" | "mapping" | "stages" | "preview" | "importing" | "complete";

type LeadImportSource = "csv" | "whatsapp";

interface ImportResult {
  success: number;
  updated: number;
  stagesCreated: number;
  skipped: number;
  skippedExisting?: number;
  source: LeadImportSource;
  errors: { row: number; name: string; error: string }[];
}

type RowStatus =
  | "valid"
  | "invalid"
  | "duplicate_in_file"
  | "exists_in_workspace"
  | "ambiguous_conflict";

interface PreviewRow extends ValidationResult {
  sourceRowNumber: number;
  normalizedPhone: string | null;
  normalizedExtraPhones: string[];
  normalizedEmail: string | null;
  status: RowStatus;
  existingLeadId: string | null;
}

interface ExistingLeadRecord {
  id: string;
  phone: string | null;
  email: string | null;
  funnel_id?: string | null;
}

interface DuplicateGroup {
  key: string;
  members: {
    sourceRowNumber: number;
    name: string;
    email: string | null;
    phone: string;
  }[];
}

const EXISTS_ERROR =
  "Lead ja existe no workspace. Ative 'Atualizar existentes por email/telefone' para atualizar.";

const duplicateErrorMessage = (keptRow: number) =>
  `Telefone repetido no arquivo (linha mantida: ${keptRow}).`;

const isDuplicateError = (err: string) => err.startsWith("Telefone repetido no arquivo");


export function LeadCsvImportDialog({
  open,
  onOpenChange,
  workspaceId,
  funnelId,
  onImportComplete,
}: LeadCsvImportDialogProps) {
  const normalizeEmail = (email?: string | null): string | null => {
    if (!email) return null;
    const cleaned = email.trim().toLowerCase();
    return cleaned || null;
  };

  const normalizePhone = (phone?: string | null): string | null => normalizeImportPhone(phone);

  const yieldToUi = () => new Promise<void>((resolve) => setTimeout(resolve, 0));


  const chunkArray = <T,>(items: T[], size: number): T[][] => {
    if (items.length === 0) return [];
    const chunks: T[][] = [];
    for (let i = 0; i < items.length; i += size) {
      chunks.push(items.slice(i, i + size));
    }
    return chunks;
  };

  const { toast } = useToast();
  const [step, setStep] = useState<ImportStep>("upload");
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [mappings, setMappings] = useState<ColumnMapping[]>([]);
  const [validatedRows, setValidatedRows] = useState<PreviewRow[]>([]);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  
  // Stage mapping
  const [existingStages, setExistingStages] = useState<ExistingStage[]>([]);
  const [stageInfos, setStageInfos] = useState<StageInfo[]>([]);

  const [salesFunnels, setSalesFunnels] = useState<
    { id: string; name: string; is_default: boolean | null; order_index: number }[]
  >([]);
  const [importTargetFunnelId, setImportTargetFunnelId] = useState<string | null>(null);
  const [importDefaultStageSlug, setImportDefaultStageSlug] = useState<string>("novo");
  const [previewFunnelBusy, setPreviewFunnelBusy] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [prepareStatus, setPrepareStatus] = useState<{
    label: string;
    percent: number;
    detail?: string;
  } | null>(null);
  const [duplicateGroups, setDuplicateGroups] = useState<DuplicateGroup[]>([]);
  const [duplicateKeep, setDuplicateKeep] = useState<Record<string, number>>({});

  const [uazapiConnections, setUazapiConnections] = useState<
    { id: string; display_name: string }[]
  >([]);
  const [selectedUazapiIntegrationId, setSelectedUazapiIntegrationId] = useState<string | null>(
    null,
  );
  const [workspaceTags, setWorkspaceTags] = useState<LeadTag[]>([]);
  const [selectedImportTagIds, setSelectedImportTagIds] = useState<string[]>([]);
  const [importSource, setImportSource] = useState<LeadImportSource>("csv");
  const [whatsappSyncDetail, setWhatsappSyncDetail] = useState<string | null>(null);
  const [whatsappSyncOpen, setWhatsappSyncOpen] = useState(false);
  const [newImportTagName, setNewImportTagName] = useState("");
  const [newImportTagColor, setNewImportTagColor] = useState(TAG_COLORS[0].value);
  const [creatingImportTag, setCreatingImportTag] = useState(false);
  const [importTagsOpen, setImportTagsOpen] = useState(false);
  const [showCreateImportTag, setShowCreateImportTag] = useState(false);
  const [whatsappJobId, setWhatsappJobId] = useState<string | null>(null);
  const [whatsappJobLive, setWhatsappJobLive] = useState<UazapiContactsSyncJobStatus | null>(null);



  const availableFields = getAvailableFields();
  const mappingWarnings = useMemo(
    () =>
      parseResult
        ? getLeadImportMappingWarnings(mappings, parseResult.rows)
        : [],
    [mappings, parseResult],
  );

  const fetchExistingStages = useCallback(
    async (targetFunnelId: string | null): Promise<ExistingStage[]> => {
      if (!targetFunnelId) {
        setExistingStages([]);
        setImportDefaultStageSlug("novo");
        return [];
      }

      const { data, error } = await supabase
        .from("lead_stages")
        .select("id, name, slug, color, order_index, is_system, funnel_id")
        .eq("workspace_id", workspaceId)
        .eq("funnel_id", targetFunnelId)
        .order("order_index", { ascending: true });

      if (error) throw error;

      const list = (data ?? []) as ExistingStage[];
      setExistingStages(list);
      const novo = list.find((s) => s.slug === "novo");
      const sorted = [...list].sort((a, b) => a.order_index - b.order_index);
      setImportDefaultStageSlug(novo?.slug ?? sorted[0]?.slug ?? "novo");
      return list;
    },
    [workspaceId]
  );

  useEffect(() => {
    if (!open || !workspaceId) return;
    let cancelled = false;

    (async () => {
      const { data: funnels, error: fuErr } = await supabase
        .from("sales_funnels")
        .select("id, name, is_default, order_index")
        .eq("workspace_id", workspaceId)
        .order("is_default", { ascending: false })
        .order("order_index", { ascending: true });

      if (cancelled) return;
      if (fuErr) {
        console.error("[LeadCsvImport] failed to load funnels", fuErr, { workspaceId });
        setSalesFunnels([]);
        toast({
          title: "Não foi possível carregar seus funis",
          description: humanizeLeadImportError(fuErr),
          variant: "destructive",
        });
        return;
      }

      const list = funnels ?? [];
      setSalesFunnels(list);

      const initial =
        funnelId && list.some((f) => f.id === funnelId)
          ? funnelId
          : list.find((f) => f.is_default)?.id ?? list[0]?.id ?? null;

      setImportTargetFunnelId(initial);
      await fetchExistingStages(initial);

      const { data: tagsData, error: tagsErr } = await supabase
        .from("lead_tags")
        .select("id, name, color")
        .eq("workspace_id", workspaceId)
        .order("name", { ascending: true });
      if (!cancelled && !tagsErr) {
        setWorkspaceTags((tagsData ?? []) as LeadTag[]);
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!cancelled && user) {
        const connections = await fetchMemberAccessibleWhatsAppIntegrations(workspaceId, user.id);
        const uazapiOnly = connections.filter((c) => c.provider === "whatsapp_uazapi");
        setUazapiConnections(uazapiOnly.map((c) => ({ id: c.id, display_name: c.display_name })));
        setSelectedUazapiIntegrationId(uazapiOnly[0]?.id ?? null);
      } else if (!cancelled) {
        setUazapiConnections([]);
        setSelectedUazapiIntegrationId(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, workspaceId, funnelId, fetchExistingStages]);

  const resetDialog = useCallback(() => {
    setStep("upload");
    setParseResult(null);
    setMappings([]);
    setValidatedRows([]);
    setDuplicateGroups([]);
    setDuplicateKeep({});

    setUpdateExisting(false);
    setImporting(false);
    setImportProgress(0);
    setImportResult(null);
    setStageInfos([]);
    setPreviewFunnelBusy(false);
    setPreparing(false);
    setPrepareStatus(null);
    setSelectedImportTagIds([]);
    setImportSource("csv");
    setWhatsappSyncDetail(null);
    setWhatsappSyncOpen(false);
    setNewImportTagName("");
    setNewImportTagColor(TAG_COLORS[0].value);
    setCreatingImportTag(false);
    setImportTagsOpen(false);
    setShowCreateImportTag(false);
    setWhatsappJobId(null);
    setWhatsappJobLive(null);
  }, []);

  const applyWhatsappJobToResult = useCallback((job: UazapiContactsSyncJobStatus) => {
    setWhatsappJobLive(job);
    setImportProgress(job.percent ?? (job.status === "completed" ? 100 : 5));
    setImportResult({
      success: job.created_total,
      updated: 0,
      stagesCreated: 0,
      skipped: job.skipped_invalid_total,
      skippedExisting: job.skipped_existing_total,
      source: "whatsapp",
      errors: [],
    });
  }, []);

  useEffect(() => {
    if (!open || !whatsappJobId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      try {
        const job = await fetchUazapiContactsSyncJob(whatsappJobId);
        if (cancelled) return;
        applyWhatsappJobToResult(job);
        if (isUazapiContactsSyncTerminal(job.status)) {
          if (job.status === "completed" && job.created_total > 0) {
            onImportComplete();
          }
          return;
        }
        timer = setTimeout(poll, 2500);
      } catch {
        if (!cancelled) timer = setTimeout(poll, 4000);
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [open, whatsappJobId, applyWhatsappJobToResult, onImportComplete]);

  const handleClose = useCallback(
    (shouldRefresh: boolean = false) => {
      if (importing) return;
      resetDialog();
      onOpenChange(false);
      if (shouldRefresh) {
        onImportComplete();
      }
    },
    [importing, onOpenChange, onImportComplete, resetDialog]
  );

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const validTypes = [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    const isValidType =
      validTypes.includes(file.type) ||
      file.name.endsWith(".csv") ||
      file.name.endsWith(".xlsx");

    if (!isValidType) {
      toast({
        title: "Formato inválido",
        description: "Por favor, selecione um arquivo CSV ou Excel.",
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await parseCSV(file);

      if (result.rows.length === 0) {
        toast({
          title: "Arquivo vazio",
          description: "O arquivo não contém dados para importar.",
          variant: "destructive",
        });
        return;
      }

      setParseResult(result);
      setMappings(result.mappings);
      setStep("mapping");
    } catch (error) {
      toast({
        title: "Erro ao processar arquivo",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    }
  };

  const handleMappingChange = (csvColumn: string, dbField: string | null) => {
    setMappings((prev) =>
      prev.map((m) =>
        m.csvColumn === csvColumn
          ? { ...m, dbField: dbField === "none" ? null : dbField, autoDetected: false }
          : m
      )
    );
  };

  const handleContinueToStages = () => {
    if (!parseResult || preparing) return;

    // Verificar se nome está mapeado
    const hasNameMapping = mappings.some((m) => m.dbField === "name");
    if (!hasNameMapping) {
      toast({
        title: "Campo obrigatório",
        description: "É necessário mapear pelo menos a coluna 'Nome'.",
        variant: "destructive",
      });
      return;
    }

    // Verificar se há coluna de estágio mapeada
    const hasStageMapping = mappings.some((m) => m.dbField === "stage");
    
    if (hasStageMapping) {
      // Extrair estágios únicos do CSV
      const stages = extractUniqueStages(parseResult.rows, mappings, existingStages);
      setStageInfos(stages);
      
      // Se todos os estágios já existem, pular para preview
      const hasNewStages = stages.some((s) => s.action === "create_new");
      if (!hasNewStages) {
        handleContinueToPreview(stages).catch((error) => {
          toast({
            title: "Erro ao preparar preview",
            description: humanizeLeadImportError(error),
            variant: "destructive",
          });
        });
      } else {
        setStep("stages");
      }
    } else {
      // Sem coluna de estágio, ir direto para preview
      handleContinueToPreview([]).catch((error) => {
        toast({
          title: "Erro ao preparar preview",
          description: humanizeLeadImportError(error),
          variant: "destructive",
        });
      });
    }
  };

  const handleStageActionChange = (csvValue: string, action: "create_new" | "map_to", mapToStageId?: string) => {
    setStageInfos((prev) =>
      prev.map((s) => {
        if (s.csvValue === csvValue) {
          if (action === "map_to" && mapToStageId) {
            const targetStage = existingStages.find((es) => es.id === mapToStageId);
            return {
              ...s,
              action: "map_to",
              mapToStageId,
              mapToStageName: targetStage?.name,
              normalizedSlug: targetStage?.slug || s.normalizedSlug,
            };
          }
          return {
            ...s,
            action: "create_new",
            mapToStageId: undefined,
            mapToStageName: undefined,
            normalizedSlug: generateSlug(csvValue),
          };
        }
        return s;
      })
    );
  };

  const handleContinueToPreview = async (stages: StageInfo[] = stageInfos) => {
    if (!parseResult) return;

    setPreparing(true);
    setPrepareStatus({
      label: "Validando as linhas do arquivo",
      percent: 0,
      detail: `0 de ${parseResult.rows.length.toLocaleString("pt-BR")} linhas`,
    });
    await yieldToUi();

    try {
      // Transformar e validar linhas em blocos, liberando a UI entre eles
      // (planilhas grandes, 10k+ linhas, travavam a aba).
      const transformed: PreviewRow[] = [];
      const TRANSFORM_CHUNK = 1000;
      const totalRows = parseResult.rows.length;
      for (let start = 0; start < totalRows; start += TRANSFORM_CHUNK) {
        const slice = parseResult.rows.slice(start, start + TRANSFORM_CHUNK);
        slice.forEach((row, offset) => {
          const parsed = transformRow(row, mappings, stages);
          transformed.push({
            ...parsed,
            sourceRowNumber: start + offset + 2, // +1 header, +1 index base
            normalizedPhone: normalizePhone(parsed.data.phone),
            normalizedExtraPhones: (parsed.data.extra_phones ?? [])
              .map((phone) => normalizePhone(phone))
              .filter((phone): phone is string => Boolean(phone)),
            normalizedEmail: normalizeEmail(parsed.data.email),
            status: parsed.isValid ? "valid" : "invalid",
            existingLeadId: null,
          });
        });
        setPrepareStatus({
          label: "Validando as linhas do arquivo",
          percent: totalRows > 0 ? Math.round((transformed.length / totalRows) * 55) : 55,
          detail: `${transformed.length.toLocaleString("pt-BR")} de ${totalRows.toLocaleString(
            "pt-BR"
          )} linhas`,
        });
        await yieldToUi();
      }

      // Duplicidade local no arquivo (telefone normalizado):
      // por padrão a PRIMEIRA ocorrência é mantida; o usuário pode trocar
      // depois, na tela de revisão.
      const rowsByPhone = new Map<string, PreviewRow[]>();
      for (const row of transformed) {
        if (!row.isValid) continue;
        const phones = [row.normalizedPhone, ...row.normalizedExtraPhones].filter(Boolean) as string[];
        for (const phone of phones) {
          const list = rowsByPhone.get(phone) || [];
          list.push(row);
          rowsByPhone.set(phone, list);
        }
      }

      const groups: DuplicateGroup[] = [];
      const keepChoice: Record<string, number> = {};
      for (const [phone, rows] of rowsByPhone) {
        const uniqueRows = [...new Map(rows.map((row) => [row.sourceRowNumber, row])).values()];
        if (uniqueRows.length < 2) continue;
        keepChoice[phone] = uniqueRows[0].sourceRowNumber;
        groups.push({
          key: phone,
          members: uniqueRows.map((r) => ({
            sourceRowNumber: r.sourceRowNumber,
            name: r.data.name || "(sem nome)",
            email: r.data.email || null,
            phone: r.data.phone || phone,
          })),
        });
        uniqueRows.slice(1).forEach((row) => {
          row.status = "duplicate_in_file";
          row.isValid = false;
          row.errors = [...row.errors, duplicateErrorMessage(uniqueRows[0].sourceRowNumber)];
        });
      }
      setDuplicateGroups(groups);
      setDuplicateKeep(keepChoice);


      // Preflight em lote no workspace para reduzir I/O
      const candidateRows = transformed.filter((r) => r.isValid);
      // Busca por variantes (com/sem o 9º dígito e sem DDI) para casar leads legados.
      const uniquePhoneVariants = Array.from(
        new Set(
          candidateRows.flatMap((r) =>
            [r.normalizedPhone, ...r.normalizedExtraPhones].flatMap((phone) =>
              buildImportPhoneVariants(phone),
            ),
          ),
        )
      );
      const uniqueEmails = Array.from(
        new Set(candidateRows.map((r) => r.normalizedEmail).filter(Boolean))
      ) as string[];

      const existingByPhone = new Map<string, ExistingLeadRecord[]>();
      const existingByEmail = new Map<string, ExistingLeadRecord[]>();
      const pushMap = (map: Map<string, ExistingLeadRecord[]>, key: string, value: ExistingLeadRecord) => {
        const prev = map.get(key) || [];
        prev.push(value);
        map.set(key, prev);
      };

      const PAGE_SIZE = 1000;

      const indexLead = (lead: ExistingLeadRecord) => {
        if (lead.phone) {
          const normalized = normalizePhone(lead.phone) || lead.phone;
          pushMap(existingByPhone, normalized, lead);
        }
        if (lead.email) {
          pushMap(existingByEmail, normalizeEmail(lead.email) || lead.email, lead);
        }
      };

      // Sempre consulta só pelas chaves do arquivo (telefone/email), filtrando
      // por workspace. Evita contar/varrer o workspace inteiro sob RLS, que
      // estourava o tempo limite em workspaces grandes.
      const LOOKUP_LABEL = "Comparando com os leads deste workspace";
      {
        // Estratégia B (workspaces grandes): consulta por lotes de chaves,
        // sempre filtrando por workspace_id.
        const chunkSize = 200;
        const fetchAllByColumn = async (
          column: "phone" | "email",
          values: string[],
          fromPercent: number,
          toPercent: number
        ) => {
          if (values.length === 0) return;
          const chunks = chunkArray(values, chunkSize);
          let done = 0;
          setPrepareStatus({ label: LOOKUP_LABEL, percent: fromPercent });
          for (const chunk of chunks) {
            let offset = 0;
            // eslint-disable-next-line no-constant-condition
            while (true) {
              const { data, error } = await supabase
                .from("leads")
                .select("id, phone, email, funnel_id")
                .eq("workspace_id", workspaceId)
                .in(column, chunk)
                .order("id", { ascending: true })
                .range(offset, offset + PAGE_SIZE - 1);

              if (error) throw error;
              const page = (data || []) as ExistingLeadRecord[];
              page.forEach(indexLead);
              if (page.length < PAGE_SIZE) break;
              offset += PAGE_SIZE;
            }
            done += chunk.length;
            setPrepareStatus({
              label: LOOKUP_LABEL,
              percent:
                fromPercent +
                Math.round(Math.min(done / values.length, 1) * (toPercent - fromPercent)),
            });
            await yieldToUi();
          }
        };

        await fetchAllByColumn("phone", uniquePhoneVariants, 60, 85);
        await fetchAllByColumn("email", uniqueEmails, 85, 95);
      }

      if (workspaceId) {
        const extraRows: Array<{ lead_id: string | null; phone: string | null }> = [];
        for (const chunk of chunkArray(uniquePhoneVariants, 200)) {
          const { data, error } = await supabase
            .from("lead_phones")
            .select("lead_id, phone")
            .eq("workspace_id", workspaceId)
            .in("phone", chunk);
          if (error) throw error;
          extraRows.push(...(data ?? []));
        }
        for (const row of extraRows) {
          if (!row.phone || !row.lead_id) continue;
          const normalized = normalizePhone(row.phone) || row.phone;
          pushMap(existingByPhone, normalized, {
            id: row.lead_id,
            phone: row.phone,
            email: null,
          });
        }
      }

      setPrepareStatus({ label: "Finalizando a revisão", percent: 97 });
      await yieldToUi();


      // Regra deterministica de matching por workspace
      for (const row of transformed) {
        if (!row.isValid) continue;

        const rowPhones = [row.normalizedPhone, ...row.normalizedExtraPhones].filter(Boolean) as string[];
        const phoneLeadIds = [
          ...new Set(rowPhones.flatMap((phone) => (existingByPhone.get(phone) || []).map((lead) => lead.id))),
        ];
        const byEmail = row.normalizedEmail ? existingByEmail.get(row.normalizedEmail) || [] : [];
        if (phoneLeadIds.length > 1) {
          row.status = "ambiguous_conflict";
          row.isValid = false;
          row.errors = [
            ...row.errors,
            "Conflito: os telefones desta linha pertencem a leads diferentes.",
          ];
          continue;
        }
        const phoneLeadId = phoneLeadIds[0] || null;
        const emailLeadId = byEmail[0]?.id || null;

        if (phoneLeadId && emailLeadId && phoneLeadId !== emailLeadId) {
          row.status = "ambiguous_conflict";
          row.isValid = false;
          row.errors = [
            ...row.errors,
            "Conflito: email e telefone apontam para leads diferentes neste workspace.",
          ];
          continue;
        }

        const chosenExistingId = phoneLeadId || emailLeadId;
        if (chosenExistingId) {
          row.status = "exists_in_workspace";
          row.existingLeadId = chosenExistingId;
          if (!updateExisting) {
            row.isValid = false;
            row.errors = [
              ...row.errors,
              "Lead ja existe no workspace. Ative 'Atualizar existentes por email/telefone' para atualizar.",
            ];
          }
        }
      }

      setValidatedRows(transformed);
      setStep("preview");
    } finally {
      setPreparing(false);
      setPrepareStatus(null);
    }
  };

  /** Troca qual ocorrência de um telefone repetido será importada. */
  const handleDuplicateKeepChange = (key: string, keepRowNumber: number) => {
    setDuplicateKeep((prev) => ({ ...prev, [key]: keepRowNumber }));
    setValidatedRows((prev) => {
      const group = prev.filter((r) => r.normalizedPhone === key);
      const carrier = group.find((r) => r.existingLeadId);
      return prev.map((row) => {
        if (row.normalizedPhone !== key) return row;
        const baseErrors = row.errors.filter(
          (err) => !isDuplicateError(err) && err !== EXISTS_ERROR
        );
        if (row.sourceRowNumber !== keepRowNumber) {
          return {
            ...row,
            status: "duplicate_in_file" as RowStatus,
            isValid: false,
            existingLeadId: null,
            errors: [...baseErrors, duplicateErrorMessage(keepRowNumber)],
          };
        }
        const existingLeadId = carrier?.existingLeadId ?? null;
        if (existingLeadId) {
          return {
            ...row,
            status: "exists_in_workspace" as RowStatus,
            existingLeadId,
            isValid: updateExisting && baseErrors.length === 0,
            errors: updateExisting ? baseErrors : [...baseErrors, EXISTS_ERROR],
          };
        }
        return {
          ...row,
          status: "valid" as RowStatus,
          existingLeadId: null,
          isValid: baseErrors.length === 0,
          errors: baseErrors,
        };
      });
    });
  };

  const handlePreviewFunnelChange = async (newFunnelId: string) => {

    if (!parseResult || newFunnelId === importTargetFunnelId) return;
    setPreviewFunnelBusy(true);
    setImportTargetFunnelId(newFunnelId);
    try {
      const stagesList = await fetchExistingStages(newFunnelId);
      const hasStageMapping = mappings.some((m) => m.dbField === "stage");
      const newStageInfos = hasStageMapping
        ? extractUniqueStages(parseResult.rows, mappings, stagesList)
        : [];
      setStageInfos(newStageInfos);
      await handleContinueToPreview(newStageInfos);
    } catch (error) {
      toast({
        title: "Não foi possível atualizar o preview",
        description: humanizeLeadImportError(error),
        variant: "destructive",
      });
    } finally {
      setPreviewFunnelBusy(false);
    }
  };

  const resolveStageForImport = (item: PreviewRow): string => {
    const hasStageCol = mappings.some((m) => m.dbField === "stage");
    const s = item.data.stage;
    if (hasStageCol && s && s !== "novo") return s;
    return importDefaultStageSlug || "novo";
  };

  const validCount = validatedRows.filter((r) => r.isValid).length;
  const errorCount = validatedRows.filter((r) => !r.isValid).length;
  const duplicateCount = validatedRows.filter((r) => r.status === "duplicate_in_file").length;
  const existingCount = validatedRows.filter((r) => r.status === "exists_in_workspace").length;
  const ambiguousCount = validatedRows.filter((r) => r.status === "ambiguous_conflict").length;
  const newStagesCount = stageInfos.filter((s) => s.action === "create_new").length;

  const handleStartImport = async () => {
    if (!workspaceId) return;
    if (!importTargetFunnelId) {
      toast({
        title: "Funil obrigatório",
        description: "Selecione o funil de destino antes de importar.",
        variant: "destructive",
      });
      return;
    }

    setImporting(true);
    setStep("importing");
    setImportProgress(0);

    setImportSource("csv");
    const result: ImportResult = {
      success: 0,
      updated: 0,
      stagesCreated: 0,
      skipped: 0,
      source: "csv",
      errors: [],
    };

    try {
      // 1. Criar estágios novos primeiro
      const stagesToCreate = stageInfos.filter((s) => s.action === "create_new");
      const maxOrderIndex = existingStages.length > 0 
        ? Math.max(...existingStages.map((s) => s.order_index)) 
        : 0;
      
      const usedColors = existingStages.map((s) => s.color);
      
      for (let i = 0; i < stagesToCreate.length; i++) {
        const stageInfo = stagesToCreate[i];
        const color = getRandomColor(usedColors);
        usedColors.push(color);

        const { error } = await supabase.from("lead_stages").insert({
          workspace_id: workspaceId,
          funnel_id: importTargetFunnelId ?? null,
          name: stageInfo.csvValue,
          slug: stageInfo.normalizedSlug,
          color,
          order_index: maxOrderIndex + i + 1,
          is_system: false,
        });

        if (!error) {
          result.stagesCreated++;
        } else {
          console.warn("[LeadCsvImport] failed to create stage", {
            workspaceId,
            funnelId: importTargetFunnelId,
            stage: stageInfo.csvValue,
            code: (error as any)?.code,
            message: error.message,
          });
          toast({
            title: `Não foi possível criar o estágio "${stageInfo.csvValue}"`,
            description: `${humanizeLeadImportError(error)}. Leads desse estágio cairão no estágio padrão.`,
            variant: "destructive",
          });
        }
      }

      // 2. Importar leads (usando preflight, sem lookup por linha)
      const rowsToProcess = validatedRows.filter((r) => r.isValid);
      const rowsSkipped = validatedRows.filter((r) => !r.isValid);
      result.skipped += rowsSkipped.length;

      const rowsToUpdate = updateExisting
        ? rowsToProcess.filter((r) => r.existingLeadId)
        : [];
      const rowsToInsert = rowsToProcess.filter((r) => !r.existingLeadId);

      // Updates: por lote de concorrencia controlada (sem select por linha)
      const updateChunkSize = 40;
      for (let i = 0; i < rowsToUpdate.length; i += updateChunkSize) {
        const chunk = rowsToUpdate.slice(i, i + updateChunkSize);
        await Promise.all(
          chunk.map(async (item) => {
            try {
              const payload: any = {
                workspace_id: workspaceId,
                name: item.data.name,
                email: item.normalizedEmail,
                phone: item.normalizedPhone,
                stage: resolveStageForImport(item),
                notes: item.data.notes || null,
                metadata: item.data.metadata || {},
              };
              if (importTargetFunnelId) payload.funnel_id = importTargetFunnelId;

              const { error } = await supabase
                .from("leads")
                .update(payload)
                .eq("id", item.existingLeadId!);
              if (error) throw error;
              if (item.normalizedExtraPhones.length > 0) {
                await supabase.from("lead_phones").upsert(
                  item.normalizedExtraPhones.map((phone) => ({
                    lead_id: item.existingLeadId!,
                    workspace_id: workspaceId,
                    phone,
                    is_primary: false,
                  })),
                  { onConflict: "workspace_id,phone", ignoreDuplicates: true },
                );
              }
              result.updated++;
            } catch (error) {
              result.errors.push({
                row: item.sourceRowNumber,
                name: item.data.name || "Lead sem nome",
                error: humanizeLeadImportError(error),
              });
            }
          })
        );
        const done = Math.min(i + updateChunkSize, rowsToUpdate.length);
        const progress = rowsToProcess.length
          ? Math.round((done / rowsToProcess.length) * 100)
          : 100;
        setImportProgress(progress);
      }

      // Inserts: lote grande + fallback individual apenas no lote que falhar
      const insertBatchSize = 200;
      for (let i = 0; i < rowsToInsert.length; i += insertBatchSize) {
        const batchRows = rowsToInsert.slice(i, i + insertBatchSize);
        const payloadBatch = batchRows.map((item) => ({
          workspace_id: workspaceId,
          funnel_id: importTargetFunnelId ?? null,
          name: item.data.name,
          email: item.normalizedEmail,
          phone: item.normalizedPhone,
          stage: resolveStageForImport(item),
          notes: item.data.notes || null,
          metadata: item.data.metadata || {},
        }));

        try {
          const { data: insertedRows, error } = await supabase
            .from("leads")
            .insert(payloadBatch)
            .select("id, phone");
          if (error) throw error;
          const insertedByPhone = new Map((insertedRows ?? []).map((row) => [row.phone, row.id]));
          for (const item of batchRows) {
            const leadId = item.normalizedPhone ? insertedByPhone.get(item.normalizedPhone) : null;
            if (leadId && item.normalizedExtraPhones.length > 0) {
              await supabase.from("lead_phones").upsert(
                item.normalizedExtraPhones.map((phone) => ({
                  lead_id: leadId,
                  workspace_id: workspaceId,
                  phone,
                  is_primary: false,
                })),
                { onConflict: "workspace_id,phone", ignoreDuplicates: true },
              );
            }
          }
          result.success += payloadBatch.length;
        } catch (error) {
          console.warn("[LeadCsvImport] batch insert failed, retrying row-by-row", {
            workspaceId,
            funnelId: importTargetFunnelId,
            batchStart: i,
            batchSize: payloadBatch.length,
            code: (error as any)?.code,
            message: (error as any)?.message,
          });
          for (let j = 0; j < payloadBatch.length; j++) {
            const row = batchRows[j];
            try {
              const { error: singleError } = await supabase.from("leads").insert(payloadBatch[j]);
              if (singleError) throw singleError;
              result.success++;
            } catch (singleError) {
              console.warn("[LeadCsvImport] row insert failed", {
                workspaceId,
                funnelId: importTargetFunnelId,
                row: row.sourceRowNumber,
                code: (singleError as any)?.code,
                message: (singleError as any)?.message,
              });
              result.errors.push({
                row: row.sourceRowNumber,
                name: row.data.name || "Lead sem nome",
                error: humanizeLeadImportError(singleError),
              });
            }
          }
        }
        const done = rowsToUpdate.length + Math.min(i + insertBatchSize, rowsToInsert.length);
        const progress = rowsToProcess.length
          ? Math.round((done / rowsToProcess.length) * 100)
          : 100;
        setImportProgress(progress);
        await yieldToUi();
      }
    } catch (error: any) {
      toast({
        title: "Erro na importação",
        description: humanizeLeadImportError(error),
        variant: "destructive",
      });
    }

    setImportResult(result);
    setImporting(false);
    setStep("complete");
  };

  const toggleImportTag = (tagId: string) => {
    if (tagId.startsWith("temp-")) return;
    setSelectedImportTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId],
    );
  };

  const createImportWorkspaceTag = async () => {
    const trimmed = newImportTagName.trim();
    if (!trimmed || !workspaceId) return;

    const existing = workspaceTags.find(
      (t) => t.name.toLowerCase() === trimmed.toLowerCase() && !t.id.startsWith("temp-"),
    );
    if (existing) {
      setSelectedImportTagIds((prev) =>
        prev.includes(existing.id) ? prev : [...prev, existing.id],
      );
      setNewImportTagName("");
      toast({ title: "Tag já existe", description: "Ela foi selecionada na lista." });
      return;
    }

    setCreatingImportTag(true);
    const tempId = `temp-${crypto.randomUUID()}`;
    const optimistic: LeadTag = { id: tempId, name: trimmed, color: newImportTagColor };
    setWorkspaceTags((prev) =>
      [...prev, optimistic].sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
    );

    const { data, error } = await supabase
      .from("lead_tags")
      .insert({
        workspace_id: workspaceId,
        name: trimmed,
        color: newImportTagColor,
      })
      .select("id, name, color")
      .single();

    setCreatingImportTag(false);

    if (error) {
      setWorkspaceTags((prev) => prev.filter((t) => t.id !== tempId));
      toast({
        title: "Não foi possível criar a tag",
        description: humanizeLeadImportError(error),
        variant: "destructive",
      });
      return;
    }

    const created = data as LeadTag;
    setWorkspaceTags((prev) =>
      prev
        .map((t) => (t.id === tempId ? created : t))
        .sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
    );
    setSelectedImportTagIds((prev) => [...prev.filter((id) => id !== tempId), created.id]);
    setNewImportTagName("");
    toast({ title: "Tag criada", description: `"${created.name}" será aplicada aos novos leads.` });
  };

  const handleStartWhatsappSync = async () => {
    if (!workspaceId || !importTargetFunnelId) {
      toast({
        title: "Funil obrigatório",
        description: "Selecione o funil de destino antes de sincronizar.",
        variant: "destructive",
      });
      return;
    }
    if (!selectedUazapiIntegrationId) {
      toast({
        title: "Conexão WhatsApp",
        description: "Selecione o número de origem da sincronização.",
        variant: "destructive",
      });
      return;
    }

    const tagIds = selectedImportTagIds.filter((id) => !id.startsWith("temp-"));

    try {
      const { jobId, alreadyRunning } = await enqueueUazapiContactsSync({
        workspaceId,
        integrationId: selectedUazapiIntegrationId,
        funnelId: importTargetFunnelId,
        stageSlug: importDefaultStageSlug || "novo",
        tagIds,
      });

      setImportSource("whatsapp");
      setWhatsappJobId(jobId);
      setImportProgress(5);
      setWhatsappSyncDetail(null);
      setStep("complete");

      toast({
        title: alreadyRunning
          ? "Sincronização já em andamento"
          : "Sincronização iniciada",
        description:
          "O processamento continua em segundo plano. Você pode fechar esta janela.",
      });

      const job = await fetchUazapiContactsSyncJob(jobId);
      applyWhatsappJobToResult(job);
    } catch (error) {
      toast({
        title: "Erro ao iniciar sincronização",
        description: humanizeLeadImportError(error),
        variant: "destructive",
      });
    }
  };

  const handleDownloadTemplateCSV = () => {
    const csv = generateTemplateCSV();
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "template-leads.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadTemplateXLSX = () => {
    const blob = generateTemplateXLSX();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "template-leads.xlsx";
    link.click();
    URL.revokeObjectURL(url);
  };

  const renderUploadStep = () => {
    const hasFunnels = salesFunnels.length > 0;
    const funnelReady = hasFunnels && !!importTargetFunnelId;
    const targetFunnelName =
      salesFunnels.find((f) => f.id === importTargetFunnelId)?.name ?? null;
    const targetStageName =
      existingStages.find((s) => s.slug === importDefaultStageSlug)?.name ??
      importDefaultStageSlug;

    return (
      <div className="space-y-6 py-4">
        <div className="rounded-lg border bg-muted/30 p-4 space-y-4">
          <div>
            <p className="text-sm font-medium">1. Funil e coluna de destino</p>
            <p className="text-xs text-muted-foreground mt-1">
              Usado na importação por arquivo e na sincronização do WhatsApp. Você pode ajustar nas
              etapas seguintes ao importar CSV. No arquivo, telefone é o principal. telefone_2,
              telefone_3 e telefone_4 entram no mesmo lead e cada um pode ter a própria conversa.
            </p>
          </div>

          {!hasFunnels ? (
            <div className="flex items-start gap-2 p-3 rounded-md border border-destructive/40 bg-destructive/5">
              <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-destructive">Nenhum funil disponível</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Crie um funil de vendas antes de importar leads.
                </p>
              </div>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2 min-w-0">
                <Label htmlFor="upload-funnel">Funil de destino</Label>
                <Select
                  value={importTargetFunnelId ?? ""}
                  onValueChange={(v) => {
                    setImportTargetFunnelId(v);
                    fetchExistingStages(v).catch((error) =>
                      toast({
                        title: "Erro ao carregar estágios",
                        description: humanizeLeadImportError(error),
                        variant: "destructive",
                      })
                    );
                  }}
                >
                  <SelectTrigger id="upload-funnel" className="w-full">
                    <SelectValue placeholder="Selecione o funil" />
                  </SelectTrigger>
                  <SelectContent>
                    {salesFunnels.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.name}
                        {f.is_default ? " (padrão)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 min-w-0">
                <Label htmlFor="upload-default-stage">Estágio padrão</Label>
                <Select
                  value={importDefaultStageSlug}
                  onValueChange={setImportDefaultStageSlug}
                  disabled={!importTargetFunnelId || existingStages.length === 0}
                >
                  <SelectTrigger id="upload-default-stage" className="w-full">
                    <SelectValue placeholder="Estágio" />
                  </SelectTrigger>
                  <SelectContent>
                    {existingStages.map((st) => (
                      <SelectItem key={st.id} value={st.slug}>
                        {st.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        {uazapiConnections.length > 0 && (
          <Collapsible
            open={whatsappSyncOpen}
            onOpenChange={setWhatsappSyncOpen}
            className="rounded-lg border border-border bg-card shadow-sm"
          >
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex w-full items-center gap-3 p-4 text-left hover:bg-muted/50 rounded-lg transition-colors"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Smartphone className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">Sincronizar agenda do WhatsApp</p>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                    {funnelReady && targetFunnelName
                      ? `Contatos novos → ${targetFunnelName} · ${targetStageName}`
                      : "Defina funil e coluna no passo 1, depois escolha o número"}
                  </p>
                </div>
                <ChevronDown
                  className={cn(
                    "w-4 h-4 text-muted-foreground shrink-0 transition-transform duration-200",
                    whatsappSyncOpen && "rotate-180",
                  )}
                />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="border-t border-border">
              <div className="px-4 py-4 space-y-5">
                <Alert className="bg-muted/40 border-border">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-xs leading-relaxed">
                    A sincronização roda no servidor. Contatos que já são leads no workspace são
                    ignorados. Você pode fechar esta janela depois de iniciar.
                  </AlertDescription>
                </Alert>

                {!funnelReady && (
                  <p className="text-sm text-amber-700 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-md px-3 py-2">
                    Selecione o funil e a coluna no passo 1 acima para liberar a sincronização.
                  </p>
                )}

                <div className="space-y-2">
                  <Label htmlFor="uazapi-connection" className="text-sm font-medium">
                    Número conectado
                  </Label>
                  <Select
                    value={selectedUazapiIntegrationId ?? ""}
                    onValueChange={setSelectedUazapiIntegrationId}
                    disabled={!funnelReady || uazapiConnections.length <= 1}
                  >
                    <SelectTrigger id="uazapi-connection" className="w-full h-10">
                      <SelectValue placeholder="Escolha de qual WhatsApp importar" />
                    </SelectTrigger>
                    <SelectContent>
                      {uazapiConnections.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.display_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Tags nos novos leads{" "}
                    <span className="font-normal text-muted-foreground">(opcional)</span>
                  </Label>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <Popover open={importTagsOpen} onOpenChange={setImportTagsOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className={cn(
                            "group w-full sm:w-auto sm:min-w-[200px]",
                            "hover:bg-primary hover:text-primary-foreground hover:border-primary",
                            "data-[state=open]:bg-primary data-[state=open]:text-primary-foreground data-[state=open]:border-primary",
                          )}
                          disabled={!funnelReady}
                        >
                          <Tag
                            className={cn(
                              "h-4 w-4 shrink-0 text-muted-foreground transition-colors",
                              "group-hover:!text-primary-foreground group-data-[state=open]:!text-primary-foreground",
                            )}
                          />
                          <span className="flex-1 text-left">Selecionar tags</span>
                          {selectedImportTagIds.length > 0 && (
                            <Badge
                              variant="secondary"
                              className="ml-auto tabular-nums group-hover:bg-primary-foreground/20 group-hover:text-primary-foreground group-data-[state=open]:bg-primary-foreground/20 group-data-[state=open]:text-primary-foreground"
                            >
                              {selectedImportTagIds.length}
                            </Badge>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-72 p-0" align="start">
                        <div className="p-2 border-b">
                          <p className="text-xs font-medium text-muted-foreground px-1">
                            Marque as tags para aplicar nos leads criados
                          </p>
                        </div>
                        <div
                          className="max-h-52 overflow-y-auto overscroll-contain p-1"
                          onWheel={(e) => e.stopPropagation()}
                        >
                          {workspaceTags.length === 0 ? (
                            <p className="text-xs text-muted-foreground text-center py-6 px-2">
                              Nenhuma tag no workspace.
                            </p>
                          ) : (
                            workspaceTags.map((tag) => {
                              const pending = tag.id.startsWith("temp-");
                              const selected = selectedImportTagIds.includes(tag.id);
                              return (
                                <button
                                  key={tag.id}
                                  type="button"
                                  disabled={pending}
                                  onClick={() => toggleImportTag(tag.id)}
                                  className={cn(
                                    "w-full flex items-center gap-2 rounded-md px-2 py-2 text-sm text-left transition-colors",
                                    selected ? "bg-primary/10 text-foreground" : "hover:bg-muted",
                                    pending && "opacity-50",
                                  )}
                                >
                                  <div className={cn("w-2.5 h-2.5 rounded-full shrink-0", tag.color)} />
                                  <span className="flex-1 truncate">{tag.name}</span>
                                  {selected && <Check className="w-4 h-4 text-primary shrink-0" />}
                                </button>
                              );
                            })
                          )}
                        </div>
                        <div className="border-t p-2">
                          <button
                            type="button"
                            className="text-xs text-primary hover:underline px-1"
                            onClick={() => setShowCreateImportTag((v) => !v)}
                          >
                            {showCreateImportTag ? "Ocultar criação" : "+ Criar nova tag"}
                          </button>
                        </div>
                      </PopoverContent>
                    </Popover>

                    {selectedImportTagIds.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
                        {selectedImportTagIds
                          .map((id) => workspaceTags.find((t) => t.id === id))
                          .filter(Boolean)
                          .map((tag) => (
                            <LeadTagBadge key={tag!.id} tag={tag!} size="sm" />
                          ))}
                      </div>
                    )}
                  </div>

                  {showCreateImportTag && (
                    <div className="rounded-md border bg-muted/20 p-3 space-y-3">
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Input
                          value={newImportTagName}
                          onChange={(e) => setNewImportTagName(e.target.value)}
                          placeholder="Nome da nova tag"
                          disabled={!funnelReady || creatingImportTag}
                          className="flex-1 bg-background"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              createImportWorkspaceTag().catch(() => undefined);
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="secondary"
                          className="shrink-0"
                          disabled={!funnelReady || creatingImportTag || !newImportTagName.trim()}
                          onClick={() => createImportWorkspaceTag().catch(() => undefined)}
                        >
                          {creatingImportTag ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            "Criar"
                          )}
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {TAG_COLORS.map((color) => (
                          <button
                            key={color.value}
                            type="button"
                            title={color.name}
                            disabled={!funnelReady || creatingImportTag}
                            onClick={() => setNewImportTagColor(color.value)}
                            className={cn(
                              "w-6 h-6 rounded-full transition-[transform,box-shadow] duration-150",
                              color.value,
                              newImportTagColor === color.value
                                ? "ring-2 ring-offset-2 ring-primary scale-105"
                                : "hover:scale-105",
                            )}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="pt-1 space-y-2">
                  <Button
                    type="button"
                    className="w-full h-10"
                    disabled={!funnelReady || !selectedUazapiIntegrationId || importing}
                    onClick={() => handleStartWhatsappSync().catch(() => undefined)}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Iniciar sincronização da agenda
                  </Button>
                  {!funnelReady && (
                    <p className="text-xs text-center text-muted-foreground">
                      Escolha funil e coluna no passo 1 para continuar.
                    </p>
                  )}
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        <div className="flex items-center justify-center gap-4">
          <div className="h-px bg-border flex-1" />
          <span className="text-xs text-muted-foreground">ou importe por arquivo</span>
          <div className="h-px bg-border flex-1" />
        </div>

        <div>
          <p className="text-sm font-medium mb-3">2. Envie seu arquivo</p>
          <label
            className={cn(
              "group flex flex-col items-center border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200",
              funnelReady
                ? "cursor-pointer hover:bg-muted/40 hover:border-primary/40 active:scale-[0.99] active:bg-muted/60"
                : "opacity-50 pointer-events-none"
            )}
          >
            <FileSpreadsheet className="w-12 h-12 text-muted-foreground mb-4 transition-colors group-hover:text-foreground" />
            <p className="text-muted-foreground mb-4">
              {funnelReady
                ? "Arraste um arquivo CSV ou Excel aqui, ou clique para selecionar"
                : "Selecione o funil de destino acima para liberar o upload"}
            </p>
            <span
              className={cn(
                buttonVariants({ variant: "default", size: "default" }),
                "pointer-events-none shadow-sm transition-transform duration-150",
                funnelReady && "group-active:scale-95"
              )}
            >
              <Upload className="w-4 h-4" />
              Selecionar arquivo
            </span>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileUpload}
              disabled={!funnelReady}
              className="sr-only"
            />
            <p className="text-xs text-muted-foreground mt-4">
              Formatos aceitos: CSV, XLSX
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Compatível com: Salesforce, HubSpot, Pipedrive, RD Station e mais
            </p>
          </label>
        </div>

        <div className="flex items-center justify-center gap-4">
          <div className="h-px bg-border flex-1" />
          <span className="text-xs text-muted-foreground">ou baixe um template</span>
          <div className="h-px bg-border flex-1" />
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={handleDownloadTemplateCSV}
          >
            <Download className="w-4 h-4 mr-2" />
            Template CSV
          </Button>
          <Button
            variant="outline"
            className="flex-1"
            onClick={handleDownloadTemplateXLSX}
          >
            <Download className="w-4 h-4 mr-2" />
            Template XLSX
          </Button>
        </div>
      </div>
    );
  };

  const renderMappingStep = () => (
    <div className="space-y-4 py-4">
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">
          Verifique o mapeamento das colunas. Campos detectados automaticamente
          estão marcados.
        </p>
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Telefone (contato)</span> é o
          número para WhatsApp e atendimento.{" "}
          <span className="font-medium text-foreground">Nº do endereço</span> é só o
          número da casa/prédio (ex.: 100, 1200), usado junto com rua e bairro.
        </p>
      </div>

      {mappingWarnings.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <ul className="list-disc space-y-1 pl-4 text-sm">
              {mappingWarnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <ScrollArea className="h-[350px] pr-4">
        <div className="space-y-3">
          {mappings.map((mapping) => (
            <div
              key={mapping.csvColumn}
              className="flex items-center gap-3 p-3 border rounded-lg"
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">
                  {mapping.csvColumn}
                </p>
                {mapping.autoDetected && (
                  <Badge variant="secondary" className="text-xs mt-1">
                    Auto-detectado
                  </Badge>
                )}
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <Select
                value={mapping.dbField || "none"}
                onValueChange={(value) =>
                  handleMappingChange(mapping.csvColumn, value)
                }
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ignorar coluna</SelectItem>
                  {availableFields.map((field) => (
                    <SelectItem key={field.value} value={field.value}>
                      {field.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );

  const renderStagesStep = () => {
    const newStages = stageInfos.filter((s) => s.action === "create_new" || s.action === "map_to");
    const existingMatches = stageInfos.filter((s) => s.action === "use_existing");

    return (
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Detectamos estágios no seu arquivo. Novos estágios serão criados automaticamente.
          </p>
          {existingMatches.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {existingMatches.map((s) => (
                <Badge key={s.csvValue} variant="secondary" className="gap-1">
                  <CheckCircle className="w-3 h-3" />
                  {s.csvValue} → {s.existingStageName}
                </Badge>
              ))}
            </div>
          )}
        </div>

        <ScrollArea className="h-[300px] pr-4">
          <div className="space-y-3">
            {stageInfos.filter((s) => s.action !== "use_existing").map((stageInfo) => (
              <div
                key={stageInfo.csvValue}
                className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate flex items-center gap-2">
                    <Plus className="w-4 h-4 text-primary" />
                    {stageInfo.csvValue}
                  </p>
                  {stageInfo.action === "create_new" && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Será criado como: <code className="bg-background px-1 rounded">{stageInfo.normalizedSlug}</code>
                    </p>
                  )}
                  {stageInfo.action === "map_to" && (
                    <p className="text-xs text-primary mt-1">
                      Mapeado para: {stageInfo.mapToStageName}
                    </p>
                  )}
                </div>
                <Select
                  value={stageInfo.action === "map_to" ? stageInfo.mapToStageId : "create_new"}
                  onValueChange={(value) => {
                    if (value === "create_new") {
                      handleStageActionChange(stageInfo.csvValue, "create_new");
                    } else {
                      handleStageActionChange(stageInfo.csvValue, "map_to", value);
                    }
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="create_new">
                      <span className="flex items-center gap-2">
                        <Plus className="w-3 h-3" />
                        Criar novo estágio
                      </span>
                    </SelectItem>
                    {existingStages.map((stage) => (
                      <SelectItem key={stage.id} value={stage.id}>
                        <span className="flex items-center gap-2">
                          <RefreshCw className="w-3 h-3" />
                          Mapear para "{stage.name}"
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </ScrollArea>

        {newStagesCount > 0 && (
          <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-lg">
            <AlertCircle className="w-4 h-4 text-primary" />
            <p className="text-sm">
              <strong>{newStagesCount}</strong> novo(s) estágio(s) serão criados no seu Kanban.
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderPreviewStep = () => (
    <div className="space-y-4 min-w-0">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2 min-w-0">
          <Label htmlFor="import-funnel">Funil de destino</Label>
          <Select
            value={importTargetFunnelId ?? ""}
            onValueChange={(v) => handlePreviewFunnelChange(v)}
            disabled={previewFunnelBusy || salesFunnels.length === 0}
          >
            <SelectTrigger id="import-funnel" className="w-full">
              <SelectValue placeholder={salesFunnels.length ? "Selecione o funil" : "Nenhum funil no workspace"} />
            </SelectTrigger>
            <SelectContent>
              {salesFunnels.map((f) => (
                <SelectItem key={f.id} value={f.id}>
                  {f.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {previewFunnelBusy && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              Atualizando preview…
            </p>
          )}
        </div>
        <div className="space-y-2 min-w-0">
          <Label htmlFor="import-default-stage">Estágio padrão</Label>
          <Select
            value={importDefaultStageSlug}
            onValueChange={setImportDefaultStageSlug}
            disabled={!importTargetFunnelId || existingStages.length === 0 || previewFunnelBusy}
          >
            <SelectTrigger id="import-default-stage" className="w-full">
              <SelectValue placeholder="Estágio" />
            </SelectTrigger>
            <SelectContent>
              {existingStages.map((st) => (
                <SelectItem key={st.id} value={st.slug}>
                  {st.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground break-words">
            Usado quando não há coluna de estágio no arquivo ou o valor resolve para &quot;novo&quot;.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-4 flex-wrap min-w-0">
          <Badge variant="default" className="gap-1">
            <CheckCircle className="w-3 h-3" />
            {validCount} válidos
          </Badge>
          {errorCount > 0 && (
            <Badge variant="destructive" className="gap-1">
              <XCircle className="w-3 h-3" />
              {errorCount} com erro
            </Badge>
          )}
          {duplicateCount > 0 && (
            <Badge variant="secondary" className="gap-1">
              <AlertCircle className="w-3 h-3" />
              {duplicateCount} telefones repetidos no arquivo
            </Badge>
          )}
          {existingCount > 0 && (
            <Badge variant="outline" className="gap-1">
              <RefreshCw className="w-3 h-3" />
              {existingCount} ja existem no workspace
            </Badge>
          )}
          {ambiguousCount > 0 && (
            <Badge variant="destructive" className="gap-1">
              <AlertCircle className="w-3 h-3" />
              {ambiguousCount} conflitos ambiguos
            </Badge>
          )}
          {newStagesCount > 0 && (
            <Badge variant="outline" className="gap-1">
              <Plus className="w-3 h-3" />
              {newStagesCount} estágios novos
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="update-existing"
            checked={updateExisting}
            onCheckedChange={(checked) => {
              const shouldUpdate = checked === true;
              setUpdateExisting(shouldUpdate);
              setValidatedRows((prev) =>
                prev.map((row) => {
                  if (row.status !== "exists_in_workspace") return row;
                  const filteredErrors = row.errors.filter(
                    (err) =>
                      !err.includes("Lead ja existe no workspace. Ative 'Atualizar existentes por email/telefone' para atualizar.")
                  );
                  if (shouldUpdate) {
                    return {
                      ...row,
                      isValid: filteredErrors.length === 0,
                      errors: filteredErrors,
                    };
                  }
                  return {
                    ...row,
                    isValid: false,
                    errors: [
                      ...filteredErrors,
                      "Lead ja existe no workspace. Ative 'Atualizar existentes por email/telefone' para atualizar.",
                    ],
                  };
                })
              );
            }}
          />
          <Label htmlFor="update-existing" className="text-sm">
            Atualizar existentes por email/telefone
          </Label>
        </div>
      </div>

      {duplicateGroups.length > 0 && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 space-y-3 min-w-0">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium">
                {duplicateGroups.length.toLocaleString("pt-BR")} telefone(s) aparecem mais de uma vez no
                arquivo
              </p>
              <p className="text-xs text-muted-foreground">
                Escolha qual linha deve ser importada em cada caso. As demais serão ignoradas.
              </p>
            </div>
          </div>

          <div className="max-h-56 overflow-auto space-y-2 pr-1">
            {duplicateGroups.slice(0, 50).map((group) => (
              <div key={group.key} className="rounded-md border bg-background p-2 min-w-0">
                <p className="text-xs text-muted-foreground mb-1.5 truncate">
                  {group.members[0].phone} · {group.members.length} ocorrências
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {group.members.map((member) => {
                    const selected = duplicateKeep[group.key] === member.sourceRowNumber;
                    return (
                      <Button
                        key={member.sourceRowNumber}
                        type="button"
                        size="sm"
                        variant={selected ? "default" : "outline"}
                        className="h-auto py-1 px-2 text-xs max-w-full"
                        onClick={() => handleDuplicateKeepChange(group.key, member.sourceRowNumber)}
                      >
                        <span className="truncate">
                          Linha {member.sourceRowNumber} · {member.name}
                          {member.email ? ` · ${member.email}` : ""}
                        </span>
                      </Button>
                    );
                  })}
                </div>
              </div>
            ))}
            {duplicateGroups.length > 50 && (
              <p className="text-xs text-muted-foreground">
                Mostrando os 50 primeiros grupos. Nos demais, a primeira ocorrência será mantida.
              </p>
            )}
          </div>
        </div>
      )}



      <div className="w-full min-w-0 max-h-[min(50vh,360px)] overflow-auto rounded-md border">
        <Table className="min-w-[720px] w-full table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10 sticky top-0 z-[1] bg-background">Status</TableHead>
              <TableHead className="sticky top-0 z-[1] bg-background w-[28%] min-w-0">Nome</TableHead>
              <TableHead className="sticky top-0 z-[1] bg-background w-[22%] min-w-0">Email</TableHead>
              <TableHead className="sticky top-0 z-[1] bg-background w-[22%] min-w-0">Telefone</TableHead>
              <TableHead className="sticky top-0 z-[1] bg-background w-[18%] min-w-0">Estágio</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {validatedRows.slice(0, 80).map((row, index) => (
              <TableRow key={index}>
                <TableCell className="align-top">
                  {row.isValid ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-destructive" />
                  )}
                </TableCell>
                <TableCell className="font-medium min-w-0 align-top">
                  <div className="text-xs text-muted-foreground">
                    Linha {row.sourceRowNumber}
                  </div>
                  {row.data.name || (
                    <span className="text-destructive">[vazio]</span>
                  )}
                  {row.errors.length > 0 && (
                    <p className="text-xs text-destructive mt-1 break-words">
                      {row.errors.join(", ")}
                    </p>
                  )}
                </TableCell>
                <TableCell className="text-sm min-w-0 align-top break-words">
                  {row.data.email || "-"}
                </TableCell>
                <TableCell className="text-sm min-w-0 align-top break-words">
                  {row.data.phone || "-"}
                </TableCell>
                <TableCell className="min-w-0 align-top">
                  <Badge variant="outline" className="text-xs max-w-full whitespace-normal break-words">
                    {resolveStageForImport(row)}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {validatedRows.length > 80 && (
          <p className="text-center text-sm text-muted-foreground py-2 border-t bg-muted/30">
            Mostrando 80 de {validatedRows.length} leads
          </p>
        )}
      </div>

      {errorCount > 0 && (
        <div className="space-y-2 min-w-0">
          <p className="text-sm font-medium">Conflitos e erros detectados</p>
          <div className="max-h-[min(40vh,320px)] min-h-0 overflow-y-auto overflow-x-auto rounded-md border p-3 pr-2">
            <div className="space-y-2">
              {validatedRows
                .filter((row) => !row.isValid)
                .map((row) => (
                  <div
                    key={`${row.sourceRowNumber}-${row.data.name || "lead"}`}
                    className="text-xs break-words"
                  >
                    <span className="font-medium">Linha {row.sourceRowNumber}</span>
                    {" - "}
                    {row.data.name || "Lead sem nome"}: {row.errors.join(" | ")}
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderImportingStep = () => (
    <div className="space-y-6 py-8 text-center">
      <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary" />
      <div className="space-y-2">
        <p className="font-medium">
          {importSource === "whatsapp"
            ? "Sincronizando contatos do WhatsApp..."
            : "Importando leads..."}
        </p>
        <Progress value={importProgress} className="w-full max-w-sm mx-auto" />
        <p className="text-sm text-muted-foreground">
          {Math.round(importProgress)}% concluído
        </p>
        {importSource === "whatsapp" && whatsappSyncDetail && (
          <p className="text-xs text-muted-foreground max-w-sm mx-auto">{whatsappSyncDetail}</p>
        )}
      </div>
    </div>
  );

  const renderCompleteStep = () => (
    <div className="space-y-6 py-4 text-center">
      {importResult && (
        <>
          {importResult.source === "whatsapp" &&
            whatsappJobLive &&
            !isUazapiContactsSyncTerminal(whatsappJobLive.status) && (
              <div className="space-y-2">
                <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary" />
                <p className="text-lg font-medium">Sincronização em andamento</p>
                <p className="text-sm text-muted-foreground">
                  Processando em segundo plano
                  {whatsappJobLive.total_records != null
                    ? ` · ${whatsappJobLive.fetched_total.toLocaleString("pt-BR")} de ${whatsappJobLive.total_records.toLocaleString("pt-BR")} contatos`
                    : ""}
                </p>
                <Progress value={importProgress} className="max-w-sm mx-auto" />
              </div>
            )}

          {(importResult.success > 0 ||
            importResult.updated > 0 ||
            (importResult.source === "whatsapp" &&
              whatsappJobLive &&
              isUazapiContactsSyncTerminal(whatsappJobLive.status))) && (
            <div className="space-y-2">
              <CheckCircle className="w-12 h-12 mx-auto text-green-500" />
              <p className="text-lg font-medium">
                {importResult.source === "whatsapp"
                  ? whatsappJobLive?.status === "failed"
                    ? "Sincronização falhou"
                    : "Sincronização concluída!"
                  : "Importação concluída!"}
              </p>
              {importResult.source === "whatsapp" && whatsappJobLive?.last_error && (
                <p className="text-sm text-destructive">{whatsappJobLive.last_error}</p>
              )}
            </div>
          )}

          {importResult.source === "whatsapp" &&
            whatsappJobLive &&
            !isUazapiContactsSyncTerminal(whatsappJobLive.status) && (
              <p className="text-xs text-muted-foreground">
                Pode fechar esta janela. Avisamos na página de Leads quando terminar.
              </p>
            )}

          <div
            className={cn(
              "grid gap-3",
              importResult.source === "whatsapp"
                ? "grid-cols-2 sm:grid-cols-4"
                : "grid-cols-2 sm:grid-cols-5",
            )}
          >
            <div className="p-3 border rounded-lg">
              <p className="text-2xl font-bold text-foreground">
                {importResult.success}
              </p>
              <p className="text-xs text-muted-foreground">Novos leads</p>
            </div>
            {importResult.source === "csv" && (
              <div className="p-3 border rounded-lg">
                <p className="text-2xl font-bold text-foreground">
                  {importResult.updated}
                </p>
                <p className="text-xs text-muted-foreground">Atualizados</p>
              </div>
            )}
            {importResult.source === "csv" && (
              <div className="p-3 border rounded-lg">
                <p className="text-2xl font-bold text-foreground">
                  {importResult.stagesCreated}
                </p>
                <p className="text-xs text-muted-foreground">Estágios criados</p>
              </div>
            )}
            {importResult.source === "whatsapp" && (
              <div className="p-3 border rounded-lg">
                <p className="text-2xl font-bold text-foreground">
                  {importResult.skippedExisting ?? 0}
                </p>
                <p className="text-xs text-muted-foreground">Já no CRM</p>
              </div>
            )}
            <div className="p-3 border rounded-lg">
              <p className="text-2xl font-bold text-foreground">
                {importResult.skipped}
              </p>
              <p className="text-xs text-muted-foreground">
                {importResult.source === "whatsapp" ? "Inválidos" : "Ignorados"}
              </p>
            </div>
            <div className="p-3 border rounded-lg">
              <p className="text-2xl font-bold text-destructive">
                {importResult.errors.length}
              </p>
              <p className="text-xs text-muted-foreground">Erros</p>
            </div>
          </div>

          {importResult.errors.length > 0 && (
            <ScrollArea className="h-[150px] text-left">
              <div className="space-y-1 p-2 bg-destructive/5 rounded-lg">
                {importResult.errors.map((err, i) => (
                  <p key={i} className="text-xs text-destructive">
                    Linha {err.row} ({err.name}): {err.error}
                  </p>
                ))}
              </div>
            </ScrollArea>
          )}
        </>
      )}
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case "upload":
        return renderUploadStep();
      case "mapping":
        return renderMappingStep();
      case "stages":
        return renderStagesStep();
      case "preview":
        return renderPreviewStep();
      case "importing":
        return renderImportingStep();
      case "complete":
        return renderCompleteStep();
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case "upload":
        return "Importar Leads";
      case "mapping":
        return "Mapear Colunas";
      case "stages":
        return "Mapear Estágios";
      case "preview":
        return "Confirmar Importação";
      case "importing":
        return importSource === "whatsapp" ? "Sincronizando..." : "Importando...";
      case "complete":
        if (
          importSource === "whatsapp" &&
          whatsappJobLive &&
          !isUazapiContactsSyncTerminal(whatsappJobLive.status)
        ) {
          return "Sincronização em andamento";
        }
        return importSource === "whatsapp" ? "Sincronização de contatos" : "Importação Concluída";
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case "upload":
        return "Selecione um arquivo CSV ou Excel com seus leads.";
      case "mapping":
        return `${parseResult?.rows.length || 0} registros encontrados. Mapeie as colunas.`;
      case "stages":
        return "Novos estágios detectados. Configure como deseja importá-los.";
      case "preview":
        return "Revise os dados antes de importar.";
      case "importing":
        return importSource === "whatsapp"
          ? "Buscando contatos na agenda do WhatsApp e criando leads."
          : "Aguarde enquanto os leads são importados.";
      case "complete":
        if (
          importSource === "whatsapp" &&
          whatsappJobLive &&
          !isUazapiContactsSyncTerminal(whatsappJobLive.status)
        ) {
          return "Processando no servidor. Você pode fechar esta janela.";
        }
        return importSource === "whatsapp"
          ? "Resumo da sincronização de contatos."
          : "Seus leads foram importados com sucesso.";
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !importing && handleClose(!o)}>
      <DialogContent
        className={cn(
          "flex flex-col gap-0 overflow-hidden p-0",
          "w-[calc(100vw-1rem)] max-w-3xl max-h-[min(92vh,920px)]",
          "sm:rounded-lg"
        )}
      >
        <div className="flex flex-col min-h-0 flex-1 overflow-hidden max-h-[inherit]">
          <DialogHeader className="shrink-0 px-6 pt-6 pb-2 pr-14 text-left">
            <DialogTitle>{getStepTitle()}</DialogTitle>
            <DialogDescription>{getStepDescription()}</DialogDescription>
            {(() => {
              const stepsOrder: ImportStep[] = ["upload", "mapping", "stages", "preview", "importing", "complete"];
              const currentIdx = stepsOrder.indexOf(step);
              const labels: Record<ImportStep, string> = {
                upload: "Arquivo",
                mapping: "Colunas",
                stages: "Estágios",
                preview: "Revisão",
                importing: "Importando",
                complete: "Concluído",
              };
              const visibleSteps: ImportStep[] = ["upload", "mapping", "stages", "preview", "complete"];
              return (
                <div className="flex items-center gap-1.5 flex-wrap pt-3 text-[11px] font-medium">
                  {visibleSteps.map((s, i) => {
                    const idx = stepsOrder.indexOf(s);
                    const active = idx === currentIdx || (step === "importing" && s === "preview");
                    const done = idx < currentIdx && step !== "complete" ? true : (step === "complete" && s !== "complete");
                    return (
                      <span key={s} className="flex items-center gap-1.5">
                        <span
                          className={cn(
                            "px-2 py-0.5 rounded-full border",
                            active && "bg-primary text-primary-foreground border-primary",
                            !active && done && "bg-muted text-muted-foreground border-muted",
                            !active && !done && "bg-background text-muted-foreground"
                          )}
                        >
                          {i + 1}. {labels[s]}
                        </span>
                        {i < visibleSteps.length - 1 && (
                          <span className="text-muted-foreground">›</span>
                        )}
                      </span>
                    );
                  })}
                </div>
              );
            })()}
            {step !== "upload" && step !== "complete" && importTargetFunnelId && (
              <div className="flex items-center justify-between gap-2 mt-3 px-3 py-2 rounded-md border bg-muted/40">
                <div className="flex items-center gap-2 min-w-0">
                  <FileSpreadsheet className="w-4 h-4 text-muted-foreground shrink-0" />
                  <p className="text-xs text-muted-foreground truncate">
                    Importando para:{" "}
                    <span className="font-medium text-foreground">
                      {salesFunnels.find((f) => f.id === importTargetFunnelId)?.name ?? "Não selecionado"}
                    </span>
                    {existingStages.length > 0 && (
                      <>
                        {" "}· estágio padrão{" "}
                        <span className="font-medium text-foreground">
                          {existingStages.find((s) => s.slug === importDefaultStageSlug)?.name ?? importDefaultStageSlug}
                        </span>
                      </>
                    )}
                  </p>
                </div>
                {step !== "importing" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs shrink-0"
                    onClick={() => setStep("upload")}
                  >
                    Trocar funil
                  </Button>
                )}
              </div>
            )}
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-6 py-2">
            {renderStep()}
          </div>

          {preparing && prepareStatus && (
            <div className="shrink-0 border-t px-6 py-3 bg-muted/40 space-y-2">
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="flex items-center gap-2 min-w-0">
                  <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" />
                  <span className="font-medium truncate">{prepareStatus.label}</span>
                </span>
                <span className="text-muted-foreground tabular-nums shrink-0">
                  {Math.min(prepareStatus.percent, 99)}%
                </span>
              </div>
              <Progress value={Math.min(prepareStatus.percent, 99)} />
              <p className="text-xs text-muted-foreground">
                {prepareStatus.detail
                  ? `${prepareStatus.detail} · mantenha esta janela aberta.`
                  : "Isso pode levar alguns instantes. Mantenha esta janela aberta."}
              </p>
            </div>
          )}


          <DialogFooter className="shrink-0 border-t px-6 py-4 mt-0 gap-2 sm:gap-2 sm:justify-end bg-background">
          {step === "upload" && (
            <Button variant="outline" onClick={() => handleClose()}>
              Cancelar
            </Button>
          )}

          {step === "mapping" && (
            <>
              <Button variant="outline" onClick={() => setStep("upload")} disabled={preparing}>
                Voltar
              </Button>
              <Button onClick={handleContinueToStages} disabled={preparing}>
                {preparing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {preparing ? "Processando..." : "Continuar"}
              </Button>
            </>
          )}

          {step === "stages" && (
            <>
              <Button variant="outline" onClick={() => setStep("mapping")} disabled={preparing}>
                Voltar
              </Button>
              <Button
                disabled={preparing}
                onClick={() =>
                  handleContinueToPreview().catch((error) =>
                    toast({
                      title: "Erro ao preparar preview",
                      description: humanizeLeadImportError(error),
                      variant: "destructive",
                    })
                  )
                }
              >
                {preparing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {preparing ? "Processando..." : "Continuar"}
              </Button>
            </>
          )}



          {step === "preview" && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  const hasStageMapping = mappings.some((m) => m.dbField === "stage");
                  const hasNewStages = stageInfos.some((s) => s.action === "create_new");
                  if (hasStageMapping && hasNewStages) {
                    setStep("stages");
                  } else {
                    setStep("mapping");
                  }
                }}
              >
                Voltar
              </Button>
              <Button
                onClick={handleStartImport}
                disabled={validCount === 0 || !importTargetFunnelId || previewFunnelBusy}
              >
                Importar {validCount} leads
              </Button>
            </>
          )}

          {step === "complete" && (
            <Button
              onClick={() => handleClose(importResult?.success ? true : false)}
            >
              Fechar
            </Button>
          )}
        </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
